import cn from "classnames"

import { Button, Flex, Modal, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { CloseOutlined, InfoCircleOutlined } from "@/icons"
import { ExternalLinkIcon } from "@/molecules/ExternalLinkIcon"
import { InformationAlert } from "@/molecules/InformationAlert"
import { TicketCard } from "@/molecules/TicketCard"

import type { TicketTestModalProps } from "./TicketTestModal.types"

import styles from "./TicketTestModal.module.scss"

export const TicketTestModal = ({
  system = "jira",
  state,
  isLoading,
  onClose,
  onConfirmTest,
  onSuccessOk
}: TicketTestModalProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: system === "jira" ? "jiraTemplateModal" : "ticketModal"
  })
  const { t: tSystems } = useTranslation("translation", {
    keyPrefix: "ticketing.systems"
  })
  const vendor = { vendor: tSystems(system) }

  const isSuccess = state !== null && state !== "open"
  const isOpen = state !== null

  return (
    <Modal
      closable={false}
      open={isOpen}
      title={
        <Flex justify="space-between">
          <span>
            {isSuccess
              ? t("validation.successTitle", vendor)
              : t("validation.confirmTitle", vendor)}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={isSuccess ? onSuccessOk : onClose}
          />
        </Flex>
      }
      onCancel={isSuccess ? onSuccessOk : onClose}
      zIndex={1300}
      centered
      className={cn(styles.modal, styles.modalStatus)}
      footer={
        isSuccess
          ? [
              <Button key="continue" onClick={onClose}>
                {t("validation.continueEditingButton")}
              </Button>,
              <Button key="ok" type="primary" onClick={onSuccessOk}>
                {t("validation.okButton")}
              </Button>
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                {t("validation.cancelButton")}
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={isLoading}
                disabled={isLoading}
                onClick={onConfirmTest}
              >
                {t("validation.confirmButton")}
              </Button>
            ]
      }
    >
      {isSuccess ? (
        <div className={cn(styles.content, styles.contentSuccess)}>
          {state === "validated" ? (
            <Text>{t("validation.validatedMessage", vendor)}</Text>
          ) : (
            <>
              <Text>{t("validation.successMessage", vendor)}</Text>
              <TicketCard
                aside={<ExternalLinkIcon href={state.ticketUrl} />}
                ticketKey={state.ticketKey}
                ticketUrl={state.ticketUrl}
              />
            </>
          )}
        </div>
      ) : (
        <div className={styles.content}>
          <Text>{t("validation.confirmMessage", vendor)}</Text>
          <InformationAlert
            type="info"
            message={t("validation.infoMessage", vendor)}
            icon={<InfoCircleOutlined />}
          />
        </div>
      )}
    </Modal>
  )
}
